import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { tokens } from '../../public/tokens';

const baseTagStyle = {
    display: 'inline-flex',
    height: '21px',
    padding: '4px 8px',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
    borderRadius: tokens.globals['border-radius'],
    fontFamily: tokens.globals['font-family'],
    fontSize: tokens.typography.fontSize['md-12px'],
    fontWeight: tokens.typography.fontWeights.bold,
};

const fileExtensionTypeConfig = {
    PDF: {
        color: tokens.colors.neutral.white,
        background: tokens.colors.brand['tide-blue']['500'],
    },
    PNG: {
        color: tokens.colors.neutral.white,
        background: tokens.colors.brand['fresh-green']['500'],
    },
    JPEG: {
        color: tokens.colors.neutral.white,
        background: tokens.colors.brand['fresh-green']['500'],
    },
    DEFAULT: {
        color: tokens.colors.neutral.white,
        background: tokens.colors.neutral.grey['300'],
    },
};

const getFileExtensionTypeStyles = (type) => fileExtensionTypeConfig[type] || fileExtensionTypeConfig.DEFAULT;

const StyledFileExtensionTag = styled.span(({ $type = 'DEFAULT' }) => {
    const { color, background } = getFileExtensionTypeStyles($type);

    return {
        ...baseTagStyle,
        color,
        backgroundColor: background,
    };
});

/**
 * Shows a file extension (PDF, PNG, JPEG, ...) as a colored tag, falling back to a
 * neutral style for anything unrecognized.
 * @param {string} fileType The extension, e.g. "PDF"
 * @param {React.ReactNode} [children] Custom label; defaults to the extension
 *
 * normalized is always a string, so `||` rather than `??` is used below - '' is the
 * case that needs the fallback, and `??` would let an empty tag render.
 */
export const FileExtensionTag = forwardRef(({ fileType, children, ...props }, ref) => {
    const normalized = (fileType || '').toUpperCase();
    const label = children ?? (normalized || 'FILE');

    return (
        <StyledFileExtensionTag ref={ref} $type={normalized} {...props}>
            {label}
        </StyledFileExtensionTag>
    );
});

FileExtensionTag.displayName = 'FileExtensionTag';
